import { message } from 'antd';

/**
 * Handle token expiration consistently across the application
 * Clears local storage, shows message, and redirects to login
 */
export const handleTokenExpiration = () => {
  // Clear localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Show message to user
  message.error('Your session has expired. Please login again.');

  // Redirect to login page
  window.location.href = '/login';
};

/**
 * Check if a response indicates token expiration
 * @param response - Fetch response object
 * @returns boolean - True if token is expired
 */
export const isTokenExpired = (response: Response): boolean => {
  return response.status === 401;
};

/**
 * Handle fetch response with token expiration check
 * @param response - Fetch response object
 * @param onSuccess - Callback for successful response
 * @param onError - Callback for error response (excluding token expiration)
 */
export const handleFetchResponse = async (
  response: Response,
  onSuccess: (data: any) => void,
  onError: (data: any) => void
) => {
  const data = await response.json();

  if (response.ok) {
    onSuccess(data);
  } else if (isTokenExpired(response)) {
    handleTokenExpiration();
  } else {
    onError(data);
  }
};

/**
 * Authenticated fetch with progress callback (for large file downloads)
 * @param url - The download URL
 * @param onProgress - Callback for progress (0-100)
 * @returns Promise<Blob> - The downloaded file as a Blob
 */
export const fetchWithProgress = async (
  url: string,
  onProgress: (percent: number) => void
): Promise<Blob> => {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });
  
  // Check if response is not ok (including 202 status)
  if (!response.ok) {
    if (response.status === 202) {
      // File is being processed, throw specific error
      throw new Error('FILE_PROCESSING');
    }
    if (response.status === 404) {
      throw new Error('File not found');
    }
    if (response.status === 403) {
      throw new Error('Access denied');
    }
    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
  }
  
  const contentLength = response.headers.get('content-length');
  if (!contentLength) {
    // No content-length, fallback to normal blob
    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }
    return blob;
  }
  
  const total = parseInt(contentLength, 10);
  if (total === 0) {
    throw new Error('File has zero content length');
  }
  
  let loaded = 0;
  const reader = response.body?.getReader();
  const chunks: Uint8Array[] = [];
  
  if (!reader) {
    throw new Error('Failed to create response reader');
  }
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        loaded += value.length;
        onProgress(Math.floor((loaded / total) * 100));
      }
    }
    
    const blob = new Blob(chunks);
    if (!blob || blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }
    
    return blob;
  } catch (error) {
    console.error('Error during file download:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Download failed: ${errorMessage}`);
  } finally {
    reader.releaseLock();
  }
}; 